import { Button } from '@odin-labs/components';
import {
  ReaderDetails,
  ReaderName,
  type SideReader,
} from 'containers/jobsiteConfiguration/tabs/siteLayout/components';
import cn from 'classnames';
import React from 'react';

const classes = {
  panel: 'odin-flex odin-flex-col odin-gap-y-6',
  intro: 'odin-text-sm odin-text-gray-600',
  cards: 'odin-flex odin-flex-col odin-gap-4',
  card: 'odin-flex odin-w-full odin-items-center odin-justify-between odin-gap-3 odin-rounded-lg odin-border odin-bg-white odin-px-5 odin-py-4 odin-text-left',
  cardIdle: 'odin-border-gray-200 hover:odin-border-gray-400',
  cardSelected: 'odin-border-odin-primary odin-ring-1 odin-ring-odin-primary',
  cardName: 'odin-text-sm odin-font-medium odin-text-gray-900',
  cardMeta: 'odin-text-xs odin-text-gray-500',
  empty: 'odin-py-8 odin-text-center odin-text-sm odin-text-gray-500',
};

/**
 * The entry drawer's assign step: the site's readers not yet placed anywhere
 * in the layout, one to pick. The drawer's own footer carries Back and
 * Select; nothing is written until the entry is saved. Until the site's
 * readers are read the pool is unknown, not empty.
 */
export function AssignReaderStep({
  pool,
  noSiteReaders = false,
  failed = false,
  onRetry,
  selectedId,
  onSelect,
  busy,
}: {
  /** Undefined while the site's readers are being read. */
  pool: SideReader[] | undefined;
  /** The site has no readers at all, placed or not. */
  noSiteReaders?: boolean;
  /** The site's readers could not be read. */
  failed?: boolean;
  onRetry?: () => void;
  selectedId: string | null;
  onSelect: (readerDeviceId: string) => void;
  busy: boolean;
}): React.ReactElement {
  return (
    <div className={classes.panel}>
      <p className={classes.intro}>
        Choose a reader to place on this crossing. Only readers not yet placed
        in the layout are listed.
      </p>
      <div className={classes.cards}>
        {failed && (
          <div className={classes.empty}>
            <p>The site&apos;s readers could not be read.</p>
            {onRetry && (
              <Button
                text="Try again"
                size="sm"
                theme="white"
                onClick={onRetry}
              />
            )}
          </div>
        )}
        {!failed && !pool && (
          <p className={classes.empty}>Loading the site&apos;s readers…</p>
        )}
        {pool?.length === 0 && (
          <p className={classes.empty}>
            {noSiteReaders
              ? 'No readers on this site yet. Go to Site Settings > Devices and select Add controller to bring its readers onto this site.'
              : 'No unplaced readers on this site.'}
          </p>
        )}
        {pool?.map((reader) => {
          const selected = reader.id === selectedId;
          return (
            <button
              key={reader.id}
              type="button"
              aria-pressed={selected}
              className={cn(
                classes.card,
                selected ? classes.cardSelected : classes.cardIdle,
              )}
              disabled={busy}
              onClick={() => onSelect(reader.id)}
            >
              <span className="odin-min-w-0">
                <ReaderName reader={reader} className={classes.cardName} />
                <ReaderDetails reader={reader} />
              </span>
              <span className={classes.cardMeta}>{reader.portLabel}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
